import { Header } from "./componentUtils/Header";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import { VioletCard } from "./componentUtils/VioletCard";
import { makeStyles } from "@mui/styles";
import {
  DataGrid,
  gridClasses,
  GridEnrichedColDef,
  GridSortModel,
} from "@mui/x-data-grid";
import axios from "../../../../services/axios";
import { ClassExtended, DbUser, InsightsFilterCtx } from "../../../../interfaces";
import { InsightsProgressFilterContext } from "../../../../context/InsightsProgressFilterContext";
import { useHistory } from "react-router-dom";
import { FontRoboto, v2theme } from "../../../../styles/Theme";
import { FilterObj } from "../../../../types";
import { generateGtmEvent, HandleDataLayer, sanitizeData } from "../../../../Helpers";
import { DownloadableReport } from "../../../../hooks/useXLSXGenerator";
import { CourseMetadata } from "@backend-types";

const useStyles = makeStyles((theme) => ({
  dataTable: {
    borderRadius: "8px",
    border: `2px solid ${theme.palette.divider}`,
    backgroundColor: "white",
    // "& .MuiDataGrid-virtualScrollerContent": {
    //   height: "auto !important",
    // },

    "& .MuiDataGrid-row": {
      marginBottom: "0px !important",
      marginTop: "0px !important",
    },
    "& .MuiDataGrid-columnHeaderTitleContainer": {
      flexDirection: "row !important",
      fontWeight: theme.typography.fontWeightBold,
    },
    "& .MuiDataGrid-iconButtonContainer": {
      marginLeft: "auto",
    },
    "& .MuiDataGrid-columnHeaders": {
      height: "50px",
      minHeight: "unset",
      borderBottom: `2px solid ${theme.palette.divider}`,
      // border: "3px solid #E8E8E8 !important",
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontFamily: FontRoboto,
      fontSize: "1em",
      fontWeight: theme.typography.fontWeightBold,
    },
    "& .MuiDataGrid-columnHeader": {
      color: theme.palette.text.secondary,
      "&:focus, &:focus-within": {
        outline: "none",
      },
      "&:hover": {
        color: theme.palette.primary.main,
      },
      "& .MuiDataGrid-sortIcon": {
        color: theme.palette.primary.main,
      },
    },
    "& .MuiDataGrid-columnHeader--sorted": {
      color: theme.palette.primary.main,
      "& .MuiDataGrid-sortIcon": {
        color: theme.palette.primary.main,
      },
    },
    "& .MuiDataGrid-cell": {
      border: "0px",
      paddingBottom: "8px !important",
      paddingTop: "8px !important",
      "&:focus, &:focus-within": {
        outline: "none",
      },
    },
    "& .MuiDataGrid-cellContent": {
      fontFamily: FontRoboto,
      fontSize: "1em",
      fontWeight: v2theme.typography.fontWeightRegular,
      color: v2theme.palette.text.secondary,
    },
  },
  dataTableHeaderWithBorder: {
    borderRight: `2px solid ${theme.palette.divider}`,
    "& > .MuiDataGrid-columnSeparator": {
      visibility: "hidden",
    },
  },
  dataTableCellWithBorder: {
    borderRight: `2px solid ${theme.palette.divider} !important`,
  },
  dataTableHeader: {
    "& > .MuiDataGrid-columnSeparator": {
      visibility: "hidden",
    },
  },
  sortedDataTableValue: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.primary.main,
  },
}));

interface HintsAndErrorViewProps {
  classId: number;
  defaultModuleIds: string[];
  classDetails: ClassExtended;
  courseMetadata?: CourseMetadata;
}

export interface FeedbackSummary {
  tableData?: TableDataEntity[] | [];
  topTargetFeedback?: TopTargetFeedbackEntity[] | [];
}

export interface TableDataEntity {
  id: string;
  fullName: string;
  feedbackError: number;
  feedbackHint: number;
  topHintsTargets?: string[] | [];
  topErrorsTargets?: string[] | [];
  rankFactor: number;
  rank: number;
}

export interface TopTargetFeedbackEntity {
  targetId: string;
  targetName: string;
  feedbackHintTotal: number;
  feedbackErrorTotal: number;
  feedbackTotal: number;
}

const renderCellWithList = (params: any) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      {params.value.length > 2 &&
        params.value.slice(0, 3).map((ltm: string, index: number) => (
          <Typography variant="body2" key={`${index}${ltm}`}>
            {index + 1}. {ltm}
          </Typography>
        ))}
      {params.value.length > 0 &&
        params.value.length < 3 &&
        Array.from(Array(3).keys()).map((val, index) => (
          <Typography variant="body2" key={`${index}${val}-NA`}>
            {index + 1}. {params.value[index] ? params.value[index] : "N/A"}
          </Typography>
        ))}
    </Box>
  );
};
export const HintsAndErrorView = (props: HintsAndErrorViewProps) => {
  const classes = useStyles();
  const { classId, classDetails, courseMetadata } = props;
  const history = useHistory();
  const [isTableExpanded, setIsTableExpanded] = React.useState<boolean>(false);
  const [studentList, setStudentList] = React.useState<DbUser[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [feedbackDateFilter, setFeedbackDateFilter] = useState<FilterObj>(
    {} as FilterObj
  );
  const [feedbackSummary, setFeedbackSummary] = useState<FeedbackSummary>(
    {} as FeedbackSummary
  );
  const insightsProgressFilterCtx = useContext(
    InsightsProgressFilterContext
  ) as InsightsFilterCtx;

  const defaultModuleIdsRef = useRef(props.defaultModuleIds);
  defaultModuleIdsRef.current = props.defaultModuleIds;

  useEffect(() => {
    if (classId) {
      setLoading(true);
      const getStudentList = async () => {
        try {
          let response = await axios.get(`/insights/class/${classId}`);
          setStudentList(response.data);
        } catch (e) {
          console.log(e);
        }
      };
      getStudentList();
    }
  }, [classId]);

  useEffect(() => {
    setLoading(true);
    // const targetFilters = insightsProgressFilterCtx.filters
    //   .filter((filter) => filter.type === "target")
    //   .map((filter) => filter.id);

    let courseFilter = insightsProgressFilterCtx.selectedCourseFilter ?? {
      moduleIds: defaultModuleIdsRef.current,
    };
    if (!courseFilter) return;

    const getFeedbackSummary = async () => {
      let data = {
        students: studentList,
        courseFilter: courseFilter,
        startDate: feedbackDateFilter.startDate,
        endDate: feedbackDateFilter.endDate,
        classIds: [classId],
        v2Feedback: courseMetadata?.insights_page?.use_user_events_v2 ?? false,
      };
      try {
        let response = await axios.post(`/insights/feedback-summary`, data);
        response.data.tableData = response.data.tableData.map(
          (student: TableDataEntity) => {
            student.rankFactor = student.feedbackError + student.feedbackHint;
            return student;
          }
        );
        response.data.tableData.sort((a: TableDataEntity, b: TableDataEntity) => {
          return b.rankFactor - a.rankFactor;
        });
        response.data.tableData = response.data.tableData.map(
          (student: TableDataEntity, index: number) => {
            student.rank = index + 1;
            return student;
          }
        );
        setFeedbackSummary(response.data);
        setLoading(false);
      } catch (e) {
        console.log(e);
      }
    };
    if (studentList.length > 0) {
      getFeedbackSummary();
    } else {
      setLoading(false);
    }
  }, [
    studentList,
    feedbackDateFilter,
    insightsProgressFilterCtx.selectedCourseFilter,
    isTableExpanded,
    classId,
    courseMetadata?.insights_page?.use_user_events_v2,
  ]);

  const expandTable = () => {
    setIsTableExpanded(true);
  };

  const collapseTable = () => {
    setIsTableExpanded(false);
  };

  const columns: GridEnrichedColDef[] = [
    {
      field: "rank",
      headerName: "Rank",
      type: "string",
      align: "left",
      flex: 1,
      headerClassName: classes.dataTableHeader,
      renderCell: (params: any) => {
        return (
          <Typography
            sx={{
              font: "normal normal normal 14px roboto",
              fontWeight: "bold",
              color: "#5D5D5D",
            }}
            variant="h1">
            {params.value}
          </Typography>
        );
      },
    },
    {
      field: "fullName",
      headerName: "Student",
      align: "left",
      flex: 2,
      headerClassName: classes.dataTableHeaderWithBorder,
      cellClassName: classes.dataTableCellWithBorder,
    },
    {
      field: "feedbackError",
      headerName: "Errors",
      type: "number",
      align: "left",
      flex: 1,
      headerClassName: classes.dataTableHeader,
    },
    {
      field: "feedbackHint",
      headerName: "Hints",
      type: "number",
      align: "left",
      flex: 1,
      headerClassName: classes.dataTableHeader,
    },
    {
      field: "topErrorsTargets",
      headerName: "Learning targets with most errors (top 3)",
      type: "number",
      align: "left",
      flex: 3,
      headerClassName: classes.dataTableHeader,
      renderCell: renderCellWithList,
    },
    {
      field: "topHintsTargets",
      headerName: "Learning targets with most hints (top 3)",
      type: "number",
      align: "left",
      flex: 3,
      headerClassName: classes.dataTableHeader,
      renderCell: renderCellWithList,
    },
  ];

  const hintsErrorsReport = useMemo<DownloadableReport>(() => {
    const modifiedSheet1 =
      feedbackSummary.tableData?.map((item) => {
        return sanitizeData({
          Rank: item.rank,
          Student: item.fullName,
          Errors: item.feedbackError,
          Hints: item.feedbackHint,
          "Learning targets with most errors (top 3)": item.topErrorsTargets?.join(", "),
          "Learning targets with most hints (top 3)": item.topHintsTargets?.join(", "),
        });
      }) ?? [];

    console.log(feedbackSummary.topTargetFeedback);

    const modifiedSheet2 =
      feedbackSummary.topTargetFeedback?.map((item) => {
        return sanitizeData({
          Target: item.targetId,
          "Target ID": item.targetName,
          "Total Errors": item.feedbackErrorTotal,
          "Total Hints": item.feedbackHintTotal,
          "Total Hints/Errors": item.feedbackTotal,
        });
      }) ?? [];

    return {
      fileName: `${classDetails.subject} ${classDetails.period_block} - Overall Performance`,
      sheets: [
        {
          name: "Students top hints & errors",
          header: ["heading1"],
          data: modifiedSheet1,
        },
        {
          name: "Targets top hints & errors",
          header: ["heading1"],
          data: modifiedSheet2,
        },
      ],
    };
  }, [classDetails.subject, classDetails.period_block, feedbackSummary]);

  return (
    <>
      <Header
        onDateFilterChange={(filterObj) => {
          setFeedbackDateFilter(filterObj);
        }}
        title="Overall class performance"
        downloadableReport={hintsErrorsReport}
      />
      {/*Learning Targets with Most Hints and Errors*/}
      <Card
        sx={{
          width: "100%",
          height: "100%",
          border: "0px",
          borderRadius: "16px",
          padding: "32px",
          marginTop: "32px",
        }}>
        <CardContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
            <Typography
              component={"span"}
              sx={{ font: "normal normal 600 20px/24px proxima-soft", color: "#484848" }}
              variant="body2">
              Top 3 Learning Targets with Most Hints and Errors
            </Typography>
            {feedbackSummary.topTargetFeedback?.length === 0 && (
              <Typography sx={{ color: "#787676" }} variant="body2">
                No sufficient data to display. Check back as students submit work.
              </Typography>
            )}
            <Grid container columnSpacing={4}>
              {feedbackSummary.topTargetFeedback
                ?.slice(0, 3)
                .map((targetFeedback: TopTargetFeedbackEntity, index) => (
                  <Grid
                    key={`${index}-griditem-${targetFeedback.targetId}`}
                    item
                    xs={4}
                    sx={{
                      boxSizing: "border-box",
                      height: "128px",
                    }}>
                    <VioletCard>
                      <CardContent
                        sx={{
                          height: "100%",
                          padding: "8px 8px !important",
                        }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "1.5rem",
                            height: "100%",
                          }}>
                          <Typography
                            sx={{
                              font: "normal normal bold 40px/36px proxima-soft",
                              color: "#fff",
                            }}
                            variant="body2">
                            {index + 1}
                          </Typography>
                          <Box
                            sx={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              gap: "12px",
                            }}>
                            <Typography
                              sx={{
                                font: "normal normal bold 20px/27px proxima-soft",
                                color: "#fff",
                                width: "100%",
                                textAlign: "left",
                              }}>
                              {targetFeedback.targetName}
                            </Typography>
                            <Typography
                              onClick={() => {
                                HandleDataLayer(
                                  "link",
                                  "click",
                                  "internal link",
                                  "main content",
                                  `${targetFeedback.targetName}`,
                                  "view details",
                                  "onclick"
                                );
                                history.push({
                                  search: `?tab=2&targetId=${targetFeedback.targetId}&targetName=${targetFeedback.targetName}`,
                                });
                              }}
                              sx={{
                                font: "normal normal normal 16px/24px Roboto",
                                color: "#fff",
                                textAlign: "left",
                                textDecoration: "underline",
                                marginTop: "auto",
                                cursor: "pointer",
                              }}>
                              View details
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </VioletCard>
                  </Grid>
                ))}
            </Grid>
          </Box>
        </CardContent>
      </Card>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          marginTop: "32px",
          alignItems: "center",
          gap: "16px",
        }}>
        <Typography component={"span"} variant={"h3"} sx={{ marginRight: "auto" }}>
          Overview of individual student performance
        </Typography>
        <Typography
          component={"span"}
          variant={"h5"}
          sx={{
            font: "normal normal 600 20px/24px proxima-soft",
            marginRight: "auto",
            color: "#484848",
            letterSpacing: "0px",
          }}>
          Students with the most hints and errors
        </Typography>
        <Box sx={{ width: "100%" }}>
          <DataGrid
            className={classes.dataTable}
            rows={
              isTableExpanded
                ? feedbackSummary.tableData ?? []
                : feedbackSummary.tableData?.slice(0, 5) ?? []
            }
            columns={columns}
            getRowHeight={() => "auto"}
            sx={{
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
            }}
            onSortModelChange={(model: GridSortModel) => {
              const colDef = columns.find((item) => item.field === model[0].field);
              const headerName = colDef?.headerName;
              generateGtmEvent({
                event: "link",
                action: "click",
                name: "onclick",
                type: "internal link",
                region: "main content",
                section: "overview of individual student performance",
                text: `${headerName ?? model[0].field} ^ sort by`,
              });
            }}
            autoHeight
            disableSelectionOnClick
            disableColumnSelector
            disableDensitySelector
            disableColumnMenu
            hideFooter
            loading={loading}
          />
        </Box>
        {feedbackSummary?.tableData?.length! > 5 && (
          <Button
            size={"small"}
            variant="outlined"
            onClick={() => {
              if (isTableExpanded) {
                collapseTable();
              } else {
                expandTable();
              }
              HandleDataLayer(
                "link",
                "click",
                "internal link",
                "main content",
                "overview of individual student performance",
                `${isTableExpanded ? "Reduce list to 5 items" : "Show all students"}`,
                "onclick"
              );
            }}
            sx={{
              marginRight: "auto",
              borderRadius: "8px",
              marginTop: "16px",
              fontSize: "16px",
              color: "#000",
              "&:hover": {
                transform: "none",
              },
            }}>
            {isTableExpanded ? "Reduce list to 5 items" : "Show all students"}
          </Button>
        )}
      </Box>
      {loading && (
        <Backdrop open>
          <CircularProgress color={"primary"} />
        </Backdrop>
      )}
    </>
  );
};
